import React from 'react';
import { Search as SearchIcon, Menu, ChevronDown, LogOut, Moon, Sun } from 'lucide-react';
import { getOpenTasksCount, getRenewalsCount } from '../utils/dashboardMetrics';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useTheme } from '../contexts/ThemeContext';

interface NavItem {
    id: string;
    label: string;
}

interface NavGroup {
    label: string;
    items: { id: string; label: string }[];
}

interface CRMHeaderProps {
    currentView: string;
    setCurrentView: (id: string) => void;
    selectedCustomer: any;
    currentUser: any;
    isDark: boolean;
    onSearchOpen: () => void;
    customers: any[];
    tasks: any[];
    navItems?: NavItem[];
    onMobileMenuOpen?: () => void;
    handleLogout?: () => void;
}

/** Gruppierte Navigation mit Untermenüs */
function buildNavGroups(navItems: NavItem[]): (NavGroup | { id: string; label: string })[] {
    const ids = new Set(navItems.map((n) => n.id));
    const has = (id: string) => ids.has(id);

    const result: (NavGroup | { id: string; label: string })[] = [];

    result.push({ id: 'dashboard', label: 'Übersicht' });

    const crmItems = [
        { id: 'customers', label: 'Kunden' },
        { id: 'bestand', label: 'Bestand' },
    ].filter((i) => has(i.id));
    if (crmItems.length > 0) {
        result.push({ label: 'CRM', items: crmItems });
    }

    const workflowItems = [
        { id: 'roster', label: 'Dienstplan' },
        { id: 'tasks', label: 'Aufgaben' },
        { id: 'expirations', label: 'Abläufe' },
        { id: 'verlaengerung', label: 'Verlängerung' },
    ].filter((i) => has(i.id));
    if (workflowItems.length > 0) {
        result.push({ label: 'Workflows', items: workflowItems });
    }

    const financeItems = [
        { id: 'goals', label: 'Ziele' },
        { id: 'provisionen', label: 'Provisionen' },
        { id: 'boni', label: 'Boni-Rechner' },
    ].filter((i) => has(i.id));
    if (financeItems.length > 0) {
        result.push({ label: 'Finanzen', items: financeItems });
    }

    const toolsItems = [
        { id: 'birthdays', label: 'Geburtstage' },
        { id: 'phonebook', label: 'Telefonbuch' },
        { id: 'passwords', label: 'Passwörter' },
    ].filter((i) => has(i.id));
    if (toolsItems.length > 0) {
        result.push({ label: 'Tools', items: toolsItems });
    }

    const adminItems = [
        { id: 'users', label: 'Team' },
        { id: 'settings', label: 'Einstellungen' },
        { id: 'activity', label: 'Protokoll' },
        { id: 'stats', label: 'Statistik' },
        { id: 'templates', label: 'Briefvorlagen' },
        { id: 'boni_tables', label: 'Boni-Tabellen' },
        { id: 'admin', label: 'Import' },
    ].filter((i) => has(i.id));
    if (adminItems.length > 0) {
        result.push({ label: 'Verwaltung', items: adminItems });
    }

    return result;
}

export const CRMHeader = ({
    currentView,
    setCurrentView,
    currentUser,
    isDark,
    onSearchOpen,
    customers,
    tasks,
    navItems: navItemsProp,
    onMobileMenuOpen,
    handleLogout,
}: CRMHeaderProps) => {
    const { toggleTheme } = useTheme();

    const openTasks = getOpenTasksCount(tasks);
    const renewals = getRenewalsCount(customers, 30);
    const totalContrib = customers?.reduce((sum, c) => {
        const vertraege = c.verträge || c.vertraege || [];
        return sum + vertraege.reduce((s: number, v: any) => s + (parseFloat(String(v.beitrag || v.JAB || 0).replace(/[^\d,.-]/g, '').replace(',', '.')) || 0), 0);
    }, 0) || 0;

    const defaultNavItems: NavItem[] = [
        { id: 'dashboard', label: 'Übersicht' },
        { id: 'customers', label: 'Kunden' },
        { id: 'bestand', label: 'Bestand' },
        { id: 'roster', label: 'Dienstplan' },
        { id: 'goals', label: 'Ziele' },
        { id: 'tasks', label: 'Aufgaben' },
        { id: 'expirations', label: 'Abläufe' },
        { id: 'verlaengerung', label: 'Verlängerung' },
        { id: 'birthdays', label: 'Geburtstage' },
        { id: 'phonebook', label: 'Telefonbuch' },
        { id: 'passwords', label: 'Passwörter' },
        { id: 'users', label: 'Team' },
        { id: 'settings', label: 'Einstellungen' },
        { id: 'activity', label: 'Protokoll' },
        { id: 'stats', label: 'Statistik' },
        { id: 'templates', label: 'Vorlagen' },
        { id: 'boni_tables', label: 'Boni-Tabellen' },
        { id: 'admin', label: 'Import' },
        { id: 'provisionen', label: 'Provisionen' },
        { id: 'boni', label: 'Boni-Rechner' },
    ];
    const navItems = navItemsProp?.length ? navItemsProp : defaultNavItems;
    const navGroups = buildNavGroups(navItems);

    return (
        <div className="shrink-0">
            {/* Top Bar: Logo + Nav + User */}
            <header className="app-glass-header w-full flex items-center justify-between px-3 sm:px-4 md:px-6 border-b border-white/20 dark:border-white/5">
                <div className="flex items-center gap-4 md:gap-6 min-w-0">
                    {onMobileMenuOpen && (
                        <button onClick={onMobileMenuOpen} className="md:hidden p-2 rounded-xl app-glass-panel" aria-label="Menü öffnen">
                            <Menu size={22} />
                        </button>
                    )}
                    <div className="flex items-center gap-2 shrink-0">
                        <div className="w-9 h-9 rounded-xl bg-[#2F6BFF] flex items-center justify-center text-white shadow-lg">
                            <span className="font-black text-sm">AP</span>
                        </div>
                        <span className="font-bold text-lg tracking-tight hidden sm:inline text-slate-900 dark:text-white">
                            Agentur<span className="text-[#2F6BFF]">PRO</span>
                        </span>
                    </div>
                    <nav className="hidden md:flex items-center gap-1 overflow-x-auto scrollbar-hide py-3">
                        {navGroups.map((g) => {
                            if ('id' in g) {
                                const isActive = currentView === g.id;
                                return (
                                    <button
                                        key={g.id}
                                        onClick={() => setCurrentView(g.id)}
                                        className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all app-glass-panel ${isActive ? 'bg-slate-800/80 text-white dark:bg-slate-700/80' : 'hover:bg-white/30 dark:hover:bg-white/10'}`}
                                    >
                                        {g.label}
                                    </button>
                                );
                            }
                            const groupLabel = g.label;
                            const isActive = g.items.some((i) => i.id === currentView);
                            return (
                                <DropdownMenu key={groupLabel}>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all app-glass-panel ${isActive ? 'bg-slate-800/80 text-white dark:bg-slate-700/80' : 'hover:bg-white/30 dark:hover:bg-white/10'}`}
                                        >
                                            {groupLabel}
                                            <ChevronDown size={14} className={isActive ? 'text-white' : ''} />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start" className="app-glass-dropdown min-w-[180px] rounded-xl border-white/20 backdrop-blur-xl">
                                        {g.items.map((item) => (
                                            <DropdownMenuItem
                                                key={item.id}
                                                onClick={() => setCurrentView(item.id)}
                                                className={`cursor-pointer rounded-lg ${currentView === item.id ? 'bg-[#2F6BFF]/20 text-[#2F6BFF] font-semibold' : ''}`}
                                            >
                                                {item.label}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            );
                        })}
                    </nav>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button onClick={onSearchOpen} className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl text-sm app-glass-panel hover:bg-white/20 transition-all">
                        <SearchIcon size={16} />
                        <span className="hidden sm:inline">Suchen</span>
                        <span className="text-[10px] opacity-60 hidden md:inline">⌘K</span>
                    </button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm bg-[#2F6BFF] text-white shadow-lg app-glass-panel">
                                {currentUser?.name ? currentUser.name[0] : '?'}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="app-glass-dropdown min-w-[160px] rounded-xl border-white/20 backdrop-blur-xl">
                            <DropdownMenuItem onClick={toggleTheme} className="cursor-pointer rounded-lg flex items-center gap-2">
                                {isDark ? <Sun size={16} /> : <Moon size={16} />}
                                {isDark ? 'Hellmodus' : 'Dunkelmodus'}
                            </DropdownMenuItem>
                            {handleLogout && (
                                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer rounded-lg text-red-600 flex items-center gap-2">
                                    <LogOut size={16} />
                                    Abmelden
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            {/* KPI-Karten */}
            <div className="app-glass-kpi px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-white/20 dark:border-white/5">
                <div className="grid grid-cols-2 lg:flex lg:flex-wrap gap-2 sm:gap-4">
                    {[
                        { label: 'Gesamtbeiträge', value: totalContrib.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €', badge: 'Jahr', badgeClass: 'bg-amber-400/30 text-amber-800 dark:text-amber-200' },
                        { label: 'Kunden', value: customers?.length ?? 0, badge: 'Gesamt', badgeClass: 'bg-blue-400/30 text-blue-800 dark:text-blue-200' },
                        { label: 'Offene Aufgaben', value: openTasks, badge: 'Diese Woche', badgeClass: 'bg-emerald-400/30 text-emerald-800 dark:text-emerald-200' },
                        { label: 'Verlängerungen', value: renewals, badge: '30 Tage', badgeClass: 'bg-amber-400/30 text-amber-800 dark:text-amber-200' },
                    ].map((k) => (
                        <div key={k.label} className="flex-1 min-w-0 lg:min-w-[160px] rounded-2xl p-3 sm:p-4 app-glass-panel">
                            <p className="text-xs font-bold uppercase tracking-wider mb-1 text-slate-500 dark:text-slate-400">{k.label}</p>
                            <p className="text-base sm:text-xl font-black text-slate-900 dark:text-white">{k.value}</p>
                            <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${k.badgeClass}`}>{k.badge}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
